import { Button } from '@authentic/loyalty-pwa/components';
import { TextField } from '@authentic/loyalty-pwa/components/Form/TextField';
import { UserServiceContext } from '@authentic/loyalty-pwa/providers';
import { objectIsEmpty } from '@authentic/loyalty-pwa/util/helpers';
import { FunctionalComponent, h } from 'preact';
import { useContext, useState } from 'preact/hooks';
import { Controller, useForm } from 'react-hook-form';

import { ErrorMessage } from '../../../../components/Error';
import { ChangePasswordContentStyled, ChangePasswordPageStyled, FormControlStyled, FormHint, FormStyled, FormTitleStyled, SignInButtons } from './change-password.style';
import { BackIcon } from '../../../../icons/Back';
import { link, route, Typography } from '@authentic/loyalty-pwa/util';
import { Box } from '@authentic/loyalty-pwa/util/Box';
import { Link } from 'preact-router/match';
import { Snackbar, SnackbarType } from '../../../../components/snackbar/snackbar';
import { DelYeahContainer } from '../../../../containers/DelYeahContainer';
import { ChangePasswordProfileResult } from '@authentic/loyalty-pwa/interfaces';

export const ChangePasswordProfilePage: FunctionalComponent = (props) => {
  const currentPasswordTextFieldID: string = 'currentPassword';
  const passwordConfirmationTextFieldID: string = 'passwordConfirmation';
  const passwordTextFieldID: string = 'password';
  const htmlTitle: string = 'Change Del Yeah! Rewards Password';
  const htmlMetaDescription: string = 'Your Del Yeah! Rewards Dashboard of available rewards and offers.';
  const IDItemMenuSelected: string = '7';
  const title = 'Change my Password';
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarType, setSnackbarType] = useState(SnackbarType.Success);
  const userService = useContext(UserServiceContext);

  const {
    handleSubmit,
    getValues,
    reset,
    setValue,
    setError,
    control,
    formState: { errors, isDirty },
  } = useForm();

  const onSubmit = (data: any) => {
    setIsLoading(true);
    setErrorMessage('');
    userService
      ?.changePasswordProfile({
        password: data.password,
        password_confirmation: data.passwordConfirmation,
        current_password: data.currentPassword
      })
      .then((res: ChangePasswordProfileResult) => {
        if (res.success) {
          setSnackbarType(SnackbarType.Success);
          setSnackbarMessage('Password changed.');
        } else {
          let customFieldNameForSetError = '';

          // {"code":4078,"name":"invalid_record","message":"Current password is invalid."}
          if (res.code === 4078) {
            customFieldNameForSetError = currentPasswordTextFieldID;
          }

          // {"code":4412,"name":"invalid_params","message":"Password must contain at least 1 uppercase character."}
          // {"code":4400,"name":"invalid_record","message":"Password should not contain First Name."}
          // {"code":4412,"name":"invalid_params","message":"Password must contain at least 1 lowercase character."}
          // {"code":4400,"name":"invalid_record","message":"Password should not contain Last Name."}
          if (res.code === 4412 || res.code === 4400) {
            customFieldNameForSetError = passwordTextFieldID;
          }

          if (customFieldNameForSetError) {
            setError(customFieldNameForSetError, { message: res.message });
            setIsLoading(false);
            return;
          }

          setSnackbarType(SnackbarType.Error);
          setSnackbarMessage(res.message);
        }
        setOpenSnackbar(true);
        resetForm();
        setIsLoading(false);
      });
  };

  const resetForm = () => {
    setValue(passwordTextFieldID, '');
    setValue(passwordConfirmationTextFieldID, '');
    setValue(currentPasswordTextFieldID, '');
    setTimeout(() => {
      reset();
    }, 100);
  }

  const callbackOnCloseSnackbar = () => {
    setOpenSnackbar(false);
    console.log('callbackOnCloseSnackbar...');
  };

  const callbackOnClickSnackbar = () => {
    console.log('callbackOnClickSnackbar: ...');
  };

  const back = () => {
    route('/profile');
  }

  return (
    <DelYeahContainer htmlTitle={htmlTitle} htmlMetaDescription={htmlMetaDescription} IDItemMenuSelected={IDItemMenuSelected}>
      <ChangePasswordPageStyled>
        <ChangePasswordContentStyled>
          <FormTitleStyled onClick={back}>
            <BackIcon></BackIcon>
            <span>{title}</span>
          </FormTitleStyled>
          <FormStyled onSubmit={handleSubmit(onSubmit)}>
            <FormHint>*Required Field</FormHint>

            <FormControlStyled mb={18}>
              <Controller
                render={({ field }: any): any => (
                  <TextField
                    error={errors[currentPasswordTextFieldID]}
                    label="Current Password"
                    variant="password"
                    type="password"
                    {...field}
                    required
                    id={currentPasswordTextFieldID}
                  />
                )}
                rules={{
                  required: {
                    value: true,
                    message: 'Please enter current password',
                  },
                  minLength: {
                    value: 8,
                    message: 'Password must have at least 8 characters',
                  },
                  maxLength: {
                    value: 16,
                    message: 'Password must not be longer than 16 characters',
                  },
                }}
                control={control}
                name={currentPasswordTextFieldID}
                defaultValue=""
              />
            </FormControlStyled>

            <FormControlStyled mb={18}>
              <Controller
                render={({ field }: any): any => (
                  <TextField
                    error={errors[passwordTextFieldID]}
                    label="New Password"
                    variant="password"
                    type="password"
                    {...field}
                    required
                    id={passwordTextFieldID}
                  />
                )}
                rules={{
                  required: {
                    value: true,
                    message: 'Please enter a valid password per requirements',
                  },
                  minLength: {
                    value: 8,
                    message: 'Password must have at least 8 characters',
                  },
                  maxLength: {
                    value: 16,
                    message: 'Password must not be longer than 16 characters',
                  },
                }}
                control={control}
                name={passwordTextFieldID}
                defaultValue=""
              />
            </FormControlStyled>

            <FormControlStyled>
              <Controller
                render={({ field }: any): any => (
                  <TextField
                    error={errors[passwordConfirmationTextFieldID]}
                    label="Confirm Password"
                    variant="password"
                    type="password"
                    {...field}
                    required
                    id={passwordConfirmationTextFieldID}
                  />
                )}
                rules={{
                  required: {
                    value: true,
                    message: 'Passwords do not match',
                  },
                  validate: (value) => value === getValues('password') || 'Passwords do not match',
                }}
                control={control}
                name={passwordConfirmationTextFieldID}
                defaultValue=""
              />
            </FormControlStyled>

            <Box mb={48}>
              <Typography variant="body3">
                Password must contain 8-16 characters and all of the following: 1 upper, 1 lower, & 1
                number.
              </Typography>
            </Box>
            {(!objectIsEmpty(errors) || errorMessage) && (
              <ErrorMessage>{errorMessage ? errorMessage : 'Review highlighted errors to continue'}</ErrorMessage>
            )}

            <Button ariaLabel="NEXT" type="submit" disabled={isLoading} fullWidth>
              SUBMIT
            </Button>

          </FormStyled>
        </ChangePasswordContentStyled>
        <Snackbar
          message={snackbarMessage}
          isActive={openSnackbar}
          type={snackbarType}
          timeout={10000}
          callbackOnClose={callbackOnCloseSnackbar}
          callbackOnClick={callbackOnClickSnackbar}></Snackbar>
      </ChangePasswordPageStyled>
    </DelYeahContainer >
  );
};
